/**
 * Site Score Movement SSM-6 — Score Provenance Panel.
 *
 * Per-category breakdown of which value drove each metric: provider
 * data, doc-derived, doc-override, or provider+doc blend. Renders
 * inside the report below the Feasibility Profile section as the
 * user-visible answer to "why is this score what it is" and
 * "why didn't my Phase I ESA upload move the Regulatory bar".
 *
 * Data sources (all stamped at gen-time, immutable after):
 *  - `report.categories[].metrics[].provenance`        — SSM-3
 *  - `report.categories[].metrics[].contributingDocIds`— SSM-3
 *  - `report.categories[].metrics[].providerValue`     — SSM-3
 *  - `report.categories[].metrics[].docValue`          — SSM-3
 *  - `report.scoringInfluence`                         — SSM-5
 *  - `site.documents` (filename lookup) — passed in by the parent.
 *
 * Pre-SSM reports leave these fields undefined; the panel falls back
 * to a "Provider data only" framing for every category so it doesn't
 * silently render an empty surface.
 */
import React from 'react';
import type { SiteRagReport, RagCategory, RagMetric } from '../../../types/siteRag';
import type { SiteEntity } from '../../../types/site';
import { formatProvenanceValue } from '../../../lib/reportFormatters';
import { REPORT_SECTION_H2_CLASS, reportSectionHeadingStyle } from '../../../lib/reportSectionHeading';
import { useEffectiveReportTheme } from '../../../lib/useEffectiveReportTheme';
import { reportSectionPaddingClass } from '../../../lib/reportSectionChrome';
import { reportSectionGapClass } from '../../../lib/reportSpacingTokens';
import { roleColor, roleBorder, type ReportColorRole } from '../../../lib/reportRoleColors';
import type { ReportTheme } from '../../../types/reportTheme';

const MONO = 'var(--report-body-font, inherit)';

interface Props {
  report: SiteRagReport;
  /**
   * Source of doc filenames. Optional — when absent, doc ids render
   * as truncated short ids. Same shape used by ReportAlertBanner.
   */
  site?: { documents?: SiteEntity['documents'] } | null;
  sectionStyle?: (sectionId: string) => React.CSSProperties;
  omitSectionSidebar?: boolean;
  shellPadding?: boolean;
  sectionPageColumnCount?: (sectionId: string) => 1 | 2;
}

function provenanceLabel(p: RagCategory['provenance']): string {
  switch (p) {
    case 'doc-override': return 'Authoritative doc';
    case 'provider+doc': return 'Provider + doc';
    case 'doc':          return 'Doc only';
    case 'provider':     return 'Provider only';
    default:             return 'Provider only';
  }
}

/**
 * Provenance → editorial ROLE. doc-override (authoritative doc) reads
 * as positive; provider+doc / doc as accent; provider-only as a neutral
 * muted tag. The chip ink + low-alpha border read roleColor/roleBorder
 * so the tag follows the active preset rather than literal success/info.
 */
function provenanceChipRole(p: RagCategory['provenance']): ReportColorRole {
  switch (p) {
    case 'doc-override': return 'positive';
    case 'provider+doc': return 'accent';
    case 'doc':          return 'accent';
    case 'provider':
    default:             return 'muted';
  }
}

/** Restrained small-caps tag style — status ink + low-alpha status
 *  border, replacing the old filled tone pill. */
function editorialTagStyle(theme: ReportTheme, role: ReportColorRole): React.CSSProperties {
  return {
    fontFamily: MONO,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: roleColor(theme, role),
    border: `1px solid ${roleBorder(theme, role)}`,
  };
}

/**
 * RNH-3 — render only the filename leaf when the recorded value
 * carries a path. Full path is kept on the `title` attribute so a
 * hover reveals the source folder for disambiguation.
 *
 *   `Redhead Industrial Park/z. Archive/DealPacket.pptx`
 *     → display `DealPacket.pptx`
 *     → tooltip `Redhead Industrial Park/z. Archive/DealPacket.pptx`
 *
 * Pure / side-effect free.
 */
export function displayFileName(fileName: string | undefined | null): string {
  if (!fileName) return '';
  // Use both forward and back slashes — Windows paths sneak in via
  // user uploads on `\\` and the typed paths on the platform are `/`.
  const idx = Math.max(fileName.lastIndexOf('/'), fileName.lastIndexOf('\\'));
  if (idx < 0) return fileName;
  return fileName.slice(idx + 1);
}

function docNameById(
  docId: string,
  docs: SiteEntity['documents'] | undefined,
): { leaf: string; fullPath: string } {
  const found = (docs ?? []).find(d => d.id === docId);
  const fullPath = found?.fileName ?? `Doc ${docId.slice(0, 8)}`;
  return { leaf: displayFileName(fullPath), fullPath };
}

function formatValue(v: unknown): string {
  return formatProvenanceValue(v);
}

/**
 * Pick the metric most representative of how docs (if any) influenced
 * this category — prefer a doc-override metric first, then
 * provider+doc, otherwise undefined.
 */
function pickProvenanceMetric(cat: RagCategory): RagMetric | undefined {
  return (
    cat.metrics.find(m => m.provenance === 'doc-override') ??
    cat.metrics.find(m => m.provenance === 'provider+doc') ??
    cat.metrics.find(m => m.provenance === 'doc')
  );
}

export const ScoreProvenancePanel: React.FC<Props> = ({
  report,
  site,
  sectionStyle,
  omitSectionSidebar = false,
  shellPadding = true,
  sectionPageColumnCount,
}) => {
  // Hooks must run unconditionally on every render (React #300) — keep
  // this above ALL early returns. `useEffectiveReportTheme` takes no
  // args and reads no local state, so hoisting is behavior-preserving;
  // its result is simply discarded on the early-return paths below.
  const theme = useEffectiveReportTheme();

  const cats = report.categories;
  if (!cats || cats.length === 0) return null;
  // Don't render the panel for reports that pre-date SSM (no
  // `scoringInfluence` AND no per-metric provenance) — the surface
  // would just say "Provider only" for every category, which is what
  // users already infer. The panel is meant to make doc-influence
  // visible; nothing to show in the legacy case.
  const hasAnyProvenance =
    !!report.scoringInfluence ||
    cats.some(c => c.provenance && c.provenance !== 'provider');
  if (!hasAnyProvenance) return null;

  const docs = site?.documents;

  // RNH-3 + RNH-4 — extra source groups stamped at gen-time on
  // `report.scoringInfluence`. Optional / absent on pre-RNH reports;
  // we treat undefined as empty so the panel falls back to the
  // doc-only view without the extra section.
  const providers = report.scoringInfluence?.providers ?? [];
  const damRefs = report.scoringInfluence?.damRefs ?? [];
  const paddingClass = shellPadding ? reportSectionPaddingClass(omitSectionSidebar) : '';

  return (
    <section
      id="scoreProvenance"
      className={`${paddingClass} ${reportSectionGapClass(theme.density)}`}
      data-testid="score-provenance-panel"
      data-section="scoreProvenance"
      data-section-columns={sectionPageColumnCount ? String(sectionPageColumnCount('scoreProvenance')) : undefined}
      style={{ borderBottom: `1px solid ${roleColor(theme, 'hair')}`, ...sectionStyle?.('scoreProvenance') }}
    >
      <h2
        className={`${REPORT_SECTION_H2_CLASS} mb-3`}
        style={reportSectionHeadingStyle(theme)}
        data-heading="true"
      >
        Score Provenance
      </h2>
      <p className="text-xs mb-4 max-w-3xl" style={{ color: roleColor(theme, 'muted') }}>
        How each category's score was derived. Provider-only scores come from public
        datasets (HIFLD, FEMA, USGS, etc.); doc-influenced scores incorporate
        authoritative project documents (ISO Queue Letters, Water Rights Certs,
        Phase I ESAs, etc.).
      </p>

      <div className="grid grid-cols-1 gap-3" data-testid="score-provenance-rows">
        {cats.map((cat, catIdx) => {
          const prov = cat.provenance ?? 'provider';
          const driverMetric = pickProvenanceMetric(cat);
          const docIds = cat.contributingDocIds ?? [];
          const isLastRow = catIdx === cats.length - 1;
          return (
            <div
              key={cat.id}
              className="grid grid-cols-[220px_140px_1fr] gap-3 items-start py-2"
              style={{ borderBottom: isLastRow ? 'none' : `1px solid ${roleColor(theme, 'hair')}` }}
              data-testid={`score-provenance-row-${cat.id}`}
              data-provenance={prov}
            >
              <span
                className="text-sm font-bold uppercase tracking-wider truncate"
                style={{ color: roleColor(theme, 'ink') }}
                title={cat.label}
              >
                {cat.label}
              </span>
              <span
                className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 self-start"
                style={editorialTagStyle(theme, provenanceChipRole(prov))}
                data-testid={`score-provenance-chip-${cat.id}`}
              >
                {provenanceLabel(prov)}
              </span>
              <div className="text-[11px] leading-snug space-y-1" style={{ color: roleColor(theme, 'body') }}>
                {driverMetric && (
                  <>
                    {/* William QA 2026-06-29 — only render the row when the formatted
                        value is NON-EMPTY. A doc-only (AUTHORITATIVE DOC) category had a
                        present-but-blank providerValue/docValue, so the `!== undefined`
                        guard alone printed a bare "Provider:" / "From doc:" with no value. */}
                    {driverMetric.providerValue !== undefined &&
                      `${formatValue(driverMetric.providerValue)}`.trim() !== '' && (
                      <div data-testid={`score-provenance-provider-${cat.id}`}>
                        <span style={{ color: roleColor(theme, 'muted') }}>Provider: </span>
                        <span style={{ fontFamily: MONO }}>{formatValue(driverMetric.providerValue)}</span>
                      </div>
                    )}
                    {driverMetric.docValue !== undefined &&
                      `${formatValue(driverMetric.docValue)}`.trim() !== '' && (
                      <div data-testid={`score-provenance-doc-${cat.id}`}>
                        <span style={{ color: roleColor(theme, 'muted') }}>From doc: </span>
                        <span style={{ fontFamily: MONO }}>{formatValue(driverMetric.docValue)}</span>
                      </div>
                    )}
                  </>
                )}
                {docIds.length > 0 && (
                  <div className="pt-0.5">
                    <span style={{ color: roleColor(theme, 'muted') }}>
                      {docIds.length === 1 ? 'Contributing doc: ' : `${docIds.length} contributing docs: `}
                    </span>
                    <span style={{ color: roleColor(theme, 'body') }}>
                      {/* RNH-3 — render the filename leaf only; full path
                          remains accessible via the hover tooltip so a
                          reader can disambiguate two docs with the same
                          leaf if they're in different folders. */}
                      {docIds.slice(0, 3).map((id, i) => {
                        const { leaf, fullPath } = docNameById(id, docs);
                        return (
                          <span
                            key={id}
                            title={fullPath}
                            data-testid={`score-provenance-doc-name-${cat.id}-${i}`}
                          >
                            {leaf}{i < Math.min(docIds.length, 3) - 1 ? ', ' : ''}
                          </span>
                        );
                      })}
                      {docIds.length > 3 ? `, +${docIds.length - 3} more` : ''}
                    </span>
                  </div>
                )}
                {prov === 'provider' && docIds.length === 0 && (
                  <div className="italic" style={{ color: roleColor(theme, 'muted') }}>
                    No matching uploaded documents – upload an authoritative
                    source (e.g. ISO Queue Letter, Water Rights Cert, Phase I ESA)
                    to influence this score on the next rerun.
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {report.scoringInfluence && (
        <div className="mt-4 text-[11px]" style={{ color: roleColor(theme, 'muted') }} data-testid="score-provenance-telemetry">
          {report.scoringInfluence.categoriesAffectedByDocs.length > 0 ? (
            <>
              <span className="font-semibold" style={{ color: roleColor(theme, 'ink') }}>Docs moved scores in:</span>{' '}
              {report.scoringInfluence.categoriesAffectedByDocs.join(', ')}.
            </>
          ) : (
            <span>No uploaded documents moved category scores for this run.</span>
          )}
        </div>
      )}

      {/* RNH-3 — extra source groups (Provider data + DAM curated KB).
          Populated by RNH-4's `buildContributingGroups` at gen-time
          when the report carries provider / KB references. Hidden
          entirely when both groups are empty so pre-RNH reports stay
          visually unchanged. */}
      {(providers.length > 0 || damRefs.length > 0) && (
        <div
          className="mt-4 pt-4 space-y-3"
          style={{ borderTop: `1px solid ${roleColor(theme, 'rule')}` }}
          data-testid="score-provenance-extra-groups"
        >
          {providers.length > 0 && (
            <div data-testid="score-provenance-providers-group">
              <h4
                className="text-[11px] font-semibold uppercase tracking-wider mb-1.5"
                style={{ fontFamily: MONO, letterSpacing: '0.14em', color: roleColor(theme, 'muted') }}
              >
                Provider data ({providers.length})
              </h4>
              <div className="flex flex-wrap gap-1.5">
                {providers.map(p => (
                  <span
                    key={p.id}
                    className="text-[11px] px-2 py-0.5"
                    style={editorialTagStyle(theme, 'muted')}
                    title={p.sourceUrl ?? p.label}
                    data-testid={`score-provenance-provider-chip-${p.id}`}
                  >
                    {p.label}
                  </span>
                ))}
              </div>
            </div>
          )}
          {damRefs.length > 0 && (
            <div data-testid="score-provenance-dam-group">
              <h4
                className="text-[11px] font-semibold uppercase tracking-wider mb-1.5"
                style={{ fontFamily: MONO, letterSpacing: '0.14em', color: roleColor(theme, 'muted') }}
              >
                DAM curated references ({damRefs.length})
              </h4>
              <div className="flex flex-wrap gap-1.5">
                {damRefs.map(r => (
                  <span
                    key={r.refId}
                    className="text-[11px] px-2 py-0.5"
                    style={editorialTagStyle(theme, 'accent')}
                    title={`${r.damType} – ${r.label}`}
                    data-testid={`score-provenance-dam-chip-${r.refId}`}
                  >
                    {r.label}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
};
